import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "../../lib/api-client";

interface TenantCollectionItem {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  is_bundle: number;
  bundle_price_cents: number | null;
  is_global: number;
  is_tenant_assigned: number;
  books_count: number;
}

interface TenantResponse {
  tenant: {
    id: string;
    slug: string;
    trade_name: string;
    legal_name: string;
  };
  collections: TenantCollectionItem[];
}

function formatBRL(cents: number | null): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}

/** Superfície pública embutível do tenant (catálogo/apresentação) — coleções vinculadas e apresentação. */
export function TenantLandingPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [data, setData] = useState<TenantResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantSlug) return;
    setLoading(true);
    apiClient
      .get<TenantResponse>(`/public/tenant-collections?tenantSlug=${encodeURIComponent(tenantSlug)}`)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch(() => {
        setError("Empresa não encontrada ou inativa.");
      })
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  const companyName = data?.tenant.trade_name || tenantSlug;

  const [quickCode, setQuickCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  async function handleQuickCode(e: React.FormEvent) {
    e.preventDefault();
    if (!quickCode.trim()) return;
    setCodeLoading(true);
    setCodeError(null);
    const code = quickCode.trim().toUpperCase();

    // Se parecer voucher de livro individual ou corporativo com prefixo, permite resgatar
    try {
      const res = await apiClient.post<{ ok: boolean; tenantSlug?: string }>("/tenant/auth/collaborator-code", {
        code,
        tenantSlug,
      });
      if (res.ok) {
        window.location.href = res.tenantSlug ? `/t/${res.tenantSlug}` : "/minha-biblioteca";
        return;
      }
    } catch {
      // Se não for código corporativo global do tenant, tenta redirecionar para o resgate de voucher
      window.location.href = `/resgatar?voucher=${encodeURIComponent(code)}`;
      return;
    } finally {
      setCodeLoading(false);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: "var(--space-6)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "var(--space-3)" }}>
          <div>
            <div style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: "rgba(107, 33, 168, 0.12)", color: "#6b21a8", fontSize: "var(--text-xs)", fontWeight: 600, marginBottom: "var(--space-2)" }}>
              Portal Corporativo Datasci
            </div>
            <h1 style={{ margin: 0 }}>{companyName}</h1>
            <p className="text-muted" style={{ margin: "var(--space-1) 0 0 0" }}>
              Catálogo editorial e coleções liberadas para os colaboradores desta organização.
            </p>
          </div>
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <Link className="btn-secondary" to="/resgatar">
              Resgatar Voucher
            </Link>
            <Link className="btn" to="/login?perfil=corporate">
              Portal do Colaborador / Gestor
            </Link>
          </div>
        </div>
      </div>

      {/* Seção de Desbloqueio Rápido para Colaboradores e Clientes do Tenant */}
      <div className="card" style={{ marginBottom: "var(--space-6)", background: "linear-gradient(135deg, rgba(107, 33, 168, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)", borderColor: "rgba(107, 33, 168, 0.2)" }}>
        <h3 style={{ margin: "0 0 var(--space-2) 0", fontSize: "var(--text-base)" }}>
          🔑 Desbloqueio Imediato para Colaboradores e Clientes
        </h3>
        <p className="text-muted" style={{ fontSize: "var(--text-sm)", margin: "0 0 var(--space-4) 0" }}>
          Possui um código de acesso corporativo da {companyName}, voucher de livro ou e-mail institucional?
        </p>

        <form onSubmit={handleQuickCode} style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", alignItems: "center", maxWidth: 640 }}>
          <div style={{ flex: "1 1 280px" }}>
            <input
              type="text"
              value={quickCode}
              onChange={(e) => setQuickCode(e.target.value)}
              placeholder="Ex.: DEMO-CORP-2026 ou CORP-LIVRO-2026"
              style={{ width: "100%", padding: "var(--space-2) var(--space-3)", fontFamily: "monospace", textTransform: "uppercase" }}
            />
          </div>
          <button className="btn" type="submit" disabled={codeLoading || !quickCode.trim()}>
            {codeLoading ? "Verificando..." : "Desbloquear com Código"}
          </button>
          <Link to="/login?perfil=corporate" className="btn-secondary" style={{ whiteSpace: "nowrap" }}>
            Acessar com E-mail @
          </Link>
        </form>
        {codeError && <p role="alert" className="notice-error" style={{ marginTop: "var(--space-3)", fontSize: "var(--text-sm)" }}>{codeError}</p>}
      </div>

      {loading ? (
        <p className="text-muted">Carregando coleções liberadas...</p>
      ) : error ? (
        <div className="card" style={{ padding: "var(--space-6)", textAlign: "center" }}>
          <p className="text-muted">{error}</p>
        </div>
      ) : (
        <div>
          <h2 style={{ fontSize: "var(--text-xl)", marginBottom: "var(--space-4)" }}>Coleções Disponíveis</h2>

          {data && data.collections.length === 0 ? (
            <div className="card" style={{ padding: "var(--space-6)", textAlign: "center" }}>
              <p className="text-muted">Nenhuma coleção vinculada especificamente para este tenant no momento.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "var(--space-4)" }}>
              {data?.collections.map((col) => {
                const isExclusive = col.is_tenant_assigned === 1;
                return (
                  <div
                    key={col.id}
                    className="card"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      borderTop: isExclusive ? "4px solid #6b21a8" : "4px solid var(--color-primary, #0066cc)",
                    }}
                  >
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "var(--space-2)" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "2px 6px",
                            borderRadius: 4,
                            backgroundColor: isExclusive ? "rgba(107, 33, 168, 0.12)" : "rgba(34, 197, 94, 0.12)",
                            color: isExclusive ? "#6b21a8" : "#15803d",
                          }}
                        >
                          {isExclusive ? `🏢 Exclusiva: ${companyName}` : "🌐 Acervo Geral Datasci"}
                        </span>
                        <span className="text-muted" style={{ fontSize: "var(--text-xs)", fontWeight: 600 }}>
                          {col.books_count} {col.books_count === 1 ? "título" : "títulos"}
                        </span>
                      </div>

                      <strong style={{ fontSize: "var(--text-lg)", fontFamily: "var(--font-serif)", display: "block" }}>
                        {col.title}
                      </strong>

                      {col.description && (
                        <p className="text-muted" style={{ fontSize: "var(--text-sm)", marginTop: "var(--space-2)" }}>
                          {col.description}
                        </p>
                      )}
                    </div>

                    <div style={{ marginTop: "var(--space-4)", paddingTop: "var(--space-3)", borderTop: "1px solid var(--color-border, #e5e7eb)" }}>
                      {col.is_bundle === 1 && col.bundle_price_cents != null ? (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span className="price-tag" style={{ margin: 0, fontSize: "var(--text-base)" }}>
                            Pacote: {formatBRL(col.bundle_price_cents)}
                          </span>
                          <Link to="/login?perfil=corporate" className="btn-secondary" style={{ fontSize: "var(--text-xs)", padding: "4px 8px" }}>
                            Liberar Acesso
                          </Link>
                        </div>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Coleção Temática</span>
                          <Link to="/login?perfil=corporate" className="btn-secondary" style={{ fontSize: "var(--text-xs)", padding: "4px 8px" }}>
                            Ver Títulos
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
